/**
 * DTMF tone generation utilities
 */

export interface SipDTMF {
  digit: string
}

// Queue for DTMF tones to be played
const dtmfQueue: string[] = []
let isProcessingQueue = false
let sharedAudioContext: AudioContext | null = null

// DTMF frequency pairs for each digit
const DTMF_TABLE: Record<string, [number, number]> = {
  "1": [1209, 697],
  "2": [1336, 697],
  "3": [1477, 697],
  A: [1633, 697],
  "4": [1209, 770],
  "5": [1336, 770],
  "6": [1477, 770],
  B: [1633, 770],
  "7": [1209, 852],
  "8": [1336, 852],
  "9": [1477, 852],
  C: [1633, 852],
  "*": [1209, 941],
  "0": [1336, 941],
  "#": [1477, 941],
  D: [1633, 941]
}

/**
 * Sets the shared AudioContext to be used for all DTMF operations
 *
 * @param context - The AudioContext to use
 */
export function setSharedAudioContext(context: AudioContext): void {
  sharedAudioContext = context
}

/**
 * Gets or creates an AudioContext
 *
 * @returns The AudioContext to use
 */
export function getAudioContext(): AudioContext {
  if (sharedAudioContext) {
    // Ensure the context is running (important for mobile)
    if (sharedAudioContext.state === "suspended") {
      sharedAudioContext.resume().catch((err) => {
        console.error("Failed to resume AudioContext:", err)
      })
    }
    return sharedAudioContext
  }

  // Fallback to creating a new context if none was provided
  console.warn("No shared AudioContext provided, creating a new one")
  const newContext = new (window.AudioContext || (window as any).webkitAudioContext)()
  sharedAudioContext = newContext
  return newContext
}

/**
 * Generates DTMF tone audio buffer for a single digit
 *
 * @param digit - The digit to generate a tone for
 * @param audioContext - The AudioContext to use
 * @param duration - Duration of the tone in seconds
 * @param amplitude - Volume of the tone (0.0 to 1.0)
 * @returns AudioBuffer containing the DTMF tone
 */
export function generateDtmfToneBuffer(
  digit: string,
  audioContext: AudioContext,
  duration: number = 0.1,
  amplitude: number = 0.4
): AudioBuffer | null {
  if (!DTMF_TABLE[digit]) {
    console.error(`Invalid DTMF digit: ${digit}`)
    return null
  }

  const sampleRate = audioContext.sampleRate
  const numSamples = Math.floor(duration * sampleRate)
  const buffer = audioContext.createBuffer(1, numSamples, sampleRate)
  const channelData = buffer.getChannelData(0)

  const [freq1, freq2] = DTMF_TABLE[digit]

  // Generate the combined tone
  for (let i = 0; i < numSamples; i++) {
    const t = i / sampleRate
    // Mix the two frequencies with the specified amplitude
    channelData[i] = amplitude * (Math.sin(2 * Math.PI * freq1 * t) + Math.sin(2 * Math.PI * freq2 * t))
  }

  return buffer
}

/**
 * Plays a DTMF tone for a given digit
 *
 * @param digit - The digit to play a tone for
 * @param duration - Duration of the tone in seconds
 * @param delay - Delay after tone in seconds
 * @param amplitude - Volume of the tone (0.0 to 1.0)
 * @param providedContext - Optional AudioContext to use
 * @returns Promise that resolves when the tone is done playing
 */
export function playDtmfTone(
  digit: string,
  duration: number = 0.1,
  delay: number = 0.05,
  amplitude: number = 0.4,
  providedContext?: AudioContext
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!DTMF_TABLE[digit]) {
      reject(new Error(`Invalid DTMF digit: ${digit}`))
      return
    }

    // Use provided context or get shared context
    const audioContext = providedContext || getAudioContext()
    console.log(`[DTMF] playDtmfTone for digit "${digit}". Initial context state: ${audioContext.state}`)

    // Ensure the context is running (important for mobile)
    if (audioContext.state === "suspended") {
      console.log(`[DTMF] AudioContext is suspended. Attempting to resume for digit "${digit}".`)
      audioContext
        .resume()
        .then(() => {
          console.log(`[DTMF] AudioContext successfully resumed for digit "${digit}". New state: ${audioContext.state}`)
          playToneWithContext(audioContext, digit, duration, delay, amplitude, resolve, reject)
        })
        .catch((err) => {
          console.error(`[DTMF] Failed to resume AudioContext for digit "${digit}":`, err)
          reject(err)
        })
    } else if (audioContext.state === "running") {
      console.log(
        `[DTMF] AudioContext is already in state: ${audioContext.state} for digit "${digit}". Proceeding to play.`
      )
      playToneWithContext(audioContext, digit, duration, delay, amplitude, resolve, reject)
    } else {
      console.log(`[DTMF] AudioContext is in state: ${audioContext.state} for digit "${digit}". Cannot play.`)
    }
  })
}

/**
 * Helper function to play a tone with a given context
 */
function playToneWithContext(
  audioContext: AudioContext,
  digit: string,
  duration: number,
  delay: number,
  amplitude: number,
  resolve: (value: void | PromiseLike<void>) => void,
  reject: (reason?: any) => void
): void {
  const buffer = generateDtmfToneBuffer(digit, audioContext, duration, amplitude)

  if (!buffer) {
    reject(new Error("Failed to create audio buffer"))
    return
  }

  // Create source node and connect to destination
  const source = audioContext.createBufferSource()
  source.buffer = buffer
  source.connect(audioContext.destination)

  // Schedule tone to play and end
  source.start()

  // Resolve the promise after the tone plus delay has finished
  setTimeout(
    () => {
      resolve()
    },
    (duration + delay) * 1000
  )
}

/**
 * Add a DTMF digit to the playback queue
 *
 * @param dtmf - The DTMF object containing the digit to queue
 * @param providedContext - Optional AudioContext to use
 */
export function queueDtmfTone(dtmf: SipDTMF, providedContext?: AudioContext): void {
  if (!dtmf || !dtmf.digit || !DTMF_TABLE[dtmf.digit]) {
    console.warn(`Invalid DTMF received, not queueing: ${dtmf?.digit}`)
    return
  }

  // If a context is provided, set it as the shared context
  if (providedContext) {
    setSharedAudioContext(providedContext)
  }

  console.log(`Queueing DTMF tone: ${dtmf.digit}`)
  dtmfQueue.push(dtmf.digit)

  // Start processing the queue if it's not already being processed
  if (!isProcessingQueue) {
    processDtmfQueue()
  }
}

/**
 * Process the DTMF queue, playing each tone in sequence
 * This function will recursively call itself until the queue is empty
 */
async function processDtmfQueue(duration: number = 0.1, delay: number = 0.05, amplitude: number = 0.4): Promise<void> {
  if (dtmfQueue.length === 0) {
    isProcessingQueue = false
    return
  }

  isProcessingQueue = true
  const digit = dtmfQueue.shift() // Get the next digit from the queue

  if (!digit) {
    isProcessingQueue = false
    return
  }

  try {
    console.log(`Playing DTMF tone from queue: ${digit}`)
    await playDtmfTone(digit, duration, delay, amplitude)
    await new Promise((resolve) => setTimeout(resolve, 200))

    // Continue processing the queue after a small delay
    setTimeout(() => {
      processDtmfQueue(duration, delay, amplitude)
    }, 50) // Small delay between tones
  } catch (error) {
    console.error(`Error playing DTMF tone: ${digit}`, error)
    // Continue processing the queue even if there was an error
    processDtmfQueue(duration, delay, amplitude)
  }
}

/**
 * Clear the DTMF queue
 */
export function clearDtmfQueue(): void {
  dtmfQueue.length = 0
  isProcessingQueue = false
}

/**
 * Cleanup function to close the AudioContext
 */
export function cleanupAudioContext(): void {
  if (sharedAudioContext) {
    sharedAudioContext.close().catch((err) => {
      console.warn("Failed to close AudioContext:", err)
    })
    sharedAudioContext = null
  }
}
